import { PageVersionStoreModel, TopicLevel1UIModel, TopicLevel2UIModel } from '@examdojo/category/v2';

export interface TopicLevel1TreeItem extends TopicLevel1UIModel {
  level: 1;
}
export interface TopicLevel2TreeItem extends TopicLevel2UIModel {
  level: 2;
  code: string;
  latest_pages: PageVersionStoreModel[];
}

export type AssetTreeItem = TopicLevel1TreeItem | TopicLevel2TreeItem;

export interface AssetTreeNode<T extends AssetTreeNodeType = AssetTreeNodeType> {
  /**
   * The type of the node.
   */
  nodeType: T;

  /**
   * The model name of this node.
   *
   * A part of `node.path`.
   *
   * * For a directory, the name is the directory name.
   * * For an asset, the name is the asset name combined with asset type (to avoid potential name collisions).
   */
  modelName: string;

  /**
   * The UI name of the node.
   *
   * A part of `node.displayPathParts`.
   *
   * * For a directory, the name is the directory name.
   * * For an asset, the name is the asset name combined with invisible characters (to avoid potential name collisions).
   */
  uiName: string;

  /**
   * The children of this node.
   * This may consist of both, directories, and assets.
   *
   * * For a directory, the key is the directory name.
   * * For an asset, the key is the asset name combined with asset type (to avoid potential name collisions).
   */
  children: Record<string, AssetTreeNode>;

  /**
   *
   * The path to this node. It is the full path to the asset or directory.
   * It is a unique identifier of this node across the whole tree.
   *
   * The path is constructed from model names of all parent nodes and the model name of this node.
   *
   * The path is not the same as `EntityType.$path` of the underlying asset (if present).
   *
   * For example:
   * * `ROOT/assets/src`
   * * `ROOT/assets/src/child1/child2/my-asset.source_config`,
   * * `ROOT/assets/src/child1/child2/my-asset.source_config/sub-asset.source`,
   *
   * @deprecated Build the path from the parent nodes instead. Otherwise it's really sensitive to
   * the changes in the structure of the tree.
   */
  path: string;

  /**
   * The parent node of this node.
   * When parent is not set, it indicates that this node is the root node.
   */
  parent?: AssetTreeNode;

  /**
   * The asset that this node represents.
   *
   * Note that an asset may contain sub-assets in the `assetNode.children` property.
   */
  asset?: AssetTreeItem;

  /**
   * Defines the order of the node in the tree when the nodes are placed on the same level.
   */
  order?: number;
}

export enum AssetTreeNodeType {
  FavoritesContainer = 'FavoritesContainer',
  FavoriteAsset = 'FavoriteAsset',
  AssetsContainer = 'AssetsContainer',
  Root = 'Root',
  TopicLevel1 = 'TopicLevel1',
  TopicLevel2 = 'TopicLevel2',
  /**
   * @deprecated
   */
  SourceDirectory = 'SourceDirectory',

  /**
   * Indicates that the node is a temporary directory.
   * Such node will disappear when the asset tree node will be rerendered.
   */
  TemporaryDirectory = 'TemporaryDirectory',
}

export const NON_VISIBLE_ASSET_NODE_TYPES = [AssetTreeNodeType.Root, AssetTreeNodeType.SourceDirectory];

export const DIRECTORY_NODE_TYPES = [
  AssetTreeNodeType.TopicLevel1,
  AssetTreeNodeType.TemporaryDirectory,
  AssetTreeNodeType.SourceDirectory,
];

export const CONTAINER_NODE_TYPES = [AssetTreeNodeType.AssetsContainer, AssetTreeNodeType.FavoritesContainer];
